import type { QueryClient } from '@tanstack/react-query';
import { httpClient } from '@/shared/api/httpClient';
import { classifyApiError } from '@/shared/api/classifyApiError';
import { db } from '@/shared/db/database';
import type { SyncQueueItem } from '@/shared/db/database';
import {
  getPendingItems,
  markInProgress,
  markSuccess,
  markFailed,
} from './syncQueue';
import { resolveAddressCached } from '@/shared/geocoding/geocodingCache';

const MAX_ATTEMPTS     = 5;
const POLL_INTERVAL_MS = 15_000;

type StatusListener = (stats: { online: boolean; pending: number }) => void;

export class SyncWorker {
  private intervalId: number | null = null;
  private isProcessing = false;
  private readonly listeners = new Set<StatusListener>();
  private backgroundSyncSupported = false;

  constructor(private readonly queryClient: QueryClient) {}

  async start(): Promise<void> {
    if (this.intervalId !== null) return;

    // Feature detection: Background Sync API (Chrome/Edge only)
    if ('serviceWorker' in navigator && 'sync' in ServiceWorkerRegistration.prototype) {
      try {
        const registration = await navigator.serviceWorker.ready;
        // Background Sync supported — register sync tag, skip polling
        await registration.sync.register('sync-queue');
        this.backgroundSyncSupported = true;
        console.log('[SyncWorker] Background Sync supported — polling disabled');
        window.addEventListener('online',  this.handleOnline);
        window.addEventListener('offline', this.handleOffline);
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
        return; // Skip polling setup
      } catch (error) {
        console.warn('[SyncWorker] Background Sync registration failed, falling back to polling:', error);
      }
    }

    // Fallback: polling for iOS Safari / Firefox / browsers without Background Sync
    console.log('[SyncWorker] Background Sync NOT supported — using polling (15s interval)');
    window.addEventListener('online',  this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.intervalId = window.setInterval(() => { void this.tick(); }, POLL_INTERVAL_MS) as number;
    if (navigator.onLine) void this.tick();
  }

  async syncNow(): Promise<void> {
    await this.tick();
  }

  stop(): void {
    if (this.intervalId !== null) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
    window.removeEventListener('online',  this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.backgroundSyncSupported = false;
  }

  subscribe(listener: StatusListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Notify the Service Worker to re-register sync when new items are enqueued.
   * Called from syncQueue.enqueueSyncOperation() after adding items to Dexie.
   * Only effective when Background Sync is supported (Chrome/Edge).
   */
  async notifySyncWorker(): Promise<void> {
    if (!this.backgroundSyncSupported) return;

    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.sync.register('sync-queue');
      console.log('[SyncWorker] Background Sync re-registered after enqueue');
    } catch (error) {
      console.error('[SyncWorker] Failed to re-register sync:', error);
    }
  }

  private readonly handleOnline = () => {
    void this.notifyListeners();
    void this.tick();
  };

  private readonly handleOffline = () => {
    void this.notifyListeners();
  };

  private readonly handleVisibilityChange = () => {
    // When the page becomes visible again (e.g., back button from another tab,
    // app returning from background), retry sync if we're online and have
    // pending items. This covers edge cases where the 'online' event didn't
    // fire because the browser never detected a true offline→online transition
    // (e.g., navigating to an uncached URL while connected to WiFi without
    // internet, then navigating back).
    if (document.visibilityState === 'visible' && navigator.onLine) {
      void this.tick();
    }
  };

  private async notifyListeners(): Promise<void> {
    const pending = await db.syncQueue.where('status').equals('pending').count();
    this.listeners.forEach((l) => l({ online: navigator.onLine, pending }));
  }

  /**
   * Consolidate redundant operations PER USER before sync.
   * 
   * Rules (applied per idPerson extracted from endpoint):
   *   1. Multiple create/update for same person → keep ONLY the last one
   *   2. DELETE cancels all previous create/update for that person
   *   3. DELETE + create → both kept (legitimate recreation)
   * 
   * Examples:
   *   [POST 123, PUT 123, PUT 123] → [POST 123] (last state only)
   *   [POST 123, DELETE 123] → [DELETE 123] (create is irrelevant)
   *   [DELETE 123, POST 123] → [DELETE 123, POST 123] (recreation OK)
   *   [POST 123, POST 456] → [POST 123, POST 456] (different users, no consolidation)
   */
  private consolidateQueueByPerson(items: SyncQueueItem[]): SyncQueueItem[] {
    // Group items by idPerson (extracted from endpoint)
    const byPerson = new Map<number, SyncQueueItem[]>();
    const nonPersonItems: SyncQueueItem[] = []; // Items without idPerson (timelogs, etc.)

    for (const item of items) {
      const idPerson = this.extractIdPerson(item.endpoint);
      if (idPerson === null) {
        nonPersonItems.push(item);
      } else {
        const group = byPerson.get(idPerson) ?? [];
        group.push(item);
        byPerson.set(idPerson, group);
      }
    }

    // Consolidate each person group independently
    const consolidated: SyncQueueItem[] = [];
    
    for (const [, group] of byPerson) {
      const result = this.consolidatePersonGroup(group);
      consolidated.push(...result);
    }

    consolidated.push(...nonPersonItems);

    // Sort by createdAt to preserve chronological order
    consolidated.sort((a, b) => a.createdAt - b.createdAt);

    return consolidated;
  }

  /**
   * Consolidate operations for a SINGLE person.
   */
  private consolidatePersonGroup(group: SyncQueueItem[]): SyncQueueItem[] {
    if (group.length <= 1) return group;

    // Find last DELETE index (if any)
    let lastDeleteIdx = -1;
    for (let i = group.length - 1; i >= 0; i--) {
      if (group[i]?.operation === 'delete') {
        lastDeleteIdx = i;
        break;
      }
    }

    if (lastDeleteIdx === -1) {
      // No DELETE → keep only last create/update
      const lastItem = group[group.length - 1];
      if (!lastItem) return group; // Safety guard (should never happen)
      
      // Mark obsolete items as successful (they were superseded)
      for (let i = 0; i < group.length - 1; i++) {
        const item = group[i];
        if (item) void markSuccess(item.id);
      }
      
      return [lastItem];
    }

    // There's a DELETE
    const deleteItem = group[lastDeleteIdx];
    if (!deleteItem) return group; // Safety guard
    
    const afterDelete = group.slice(lastDeleteIdx + 1);

    // Mark everything BEFORE delete as obsolete
    for (let i = 0; i < lastDeleteIdx; i++) {
      const item = group[i];
      if (item) void markSuccess(item.id);
    }

    if (afterDelete.length === 0) {
      // DELETE is last → keep only delete
      return [deleteItem];
    }

    // DELETE + operations after → keep delete + last operation after
    const lastAfterDelete = afterDelete[afterDelete.length - 1];
    if (!lastAfterDelete) return [deleteItem]; // Safety guard
    
    // Mark intermediate operations after delete as obsolete
    for (let i = 0; i < afterDelete.length - 1; i++) {
      const item = afterDelete[i];
      if (item) void markSuccess(item.id);
    }
    
    return [deleteItem, lastAfterDelete];
  }

  /**
   * Extract idPerson from endpoint URL.
   * Returns null if endpoint doesn't contain /person/{id}/.
   * 
   * Examples:
   *   "/v1/clock/app/person/123/embed" → 123
   *   "/v1/clock/app/person/456/enroll/v2/embed" → 456
   *   "/v1/clock/app/timelog" → null
   */
  private extractIdPerson(endpoint: string): number | null {
    const match = endpoint.match(/\/person\/(\d+)/);
    return match ? Number(match[1]) : null;
  }

  private async tick(): Promise<void> {
    if (this.isProcessing) return;
    if (!navigator.onLine) return;
    this.isProcessing = true;
    try {
      const rawItems = await getPendingItems();
      
      // OPTIMIZATION: Consolidate redundant operations per user BEFORE sync
      // (e.g. [POST 123, PUT 123, PUT 123] → [POST 123])
      const items = this.consolidateQueueByPerson(rawItems);
      
      const processedModules = new Set<SyncQueueItem['module']>();
      
      for (const item of items) {
        if (!navigator.onLine) break;
        const success = await this.processItem(item);
        if (success) {
          processedModules.add(item.module);
        }
      }
      
      // Invalidate queries ONCE per module after ALL items are processed.
      // This prevents race conditions where consecutive syncs for the same
      // entity (e.g. create + update + update) would invalidate after each
      // item, causing the UI to refetch with stale intermediate data.
      for (const module of processedModules) {
        this.invalidateAfterSync(module);
      }
    } finally {
      this.isProcessing = false;
      void this.notifyListeners();
    }
  }

  private async processItem(item: SyncQueueItem): Promise<boolean> {
    await markInProgress(item.id);
    const method =
      item.operation === 'create' ? 'post' :
      item.operation === 'update' ? 'put' :
      'delete';

    let payload = item.payload;

    // Best-effort: resolve a missing location before syncing a timelog draft.
    // Never blocks or fails the sync — falls back to sending without location.
    if (item.module === 'timelog' && item.operation === 'create') {
      type TimelogSyncPayload = {
        payload_timelog?: { location?: string | null };
        coordinates?: { lat: number; lng: number };
      };
      const typedPayload = payload as TimelogSyncPayload;
      const coordinates = typedPayload.coordinates;
      const hasLocation = typeof typedPayload.payload_timelog?.location === 'string'
        && typedPayload.payload_timelog.location.length > 0;
      const hasCoordinates = coordinates != null && !(coordinates.lat === 0 && coordinates.lng === 0);

      if (!hasLocation && hasCoordinates) {
        try {
          const resolved = await resolveAddressCached(coordinates!.lat, coordinates!.lng);
          if (resolved) {
            payload = {
              ...typedPayload,
              payload_timelog: {
                ...typedPayload.payload_timelog,
                location: resolved.formattedAddress,
              },
            } as SyncQueueItem['payload'];
          }
        } catch {
          // ignore — send payload without location
        }
      }
    }

    try {
      const res = await httpClient.request<number>({ method, url: item.endpoint, data: payload });
      await markSuccess(item.id);
      
      // Fix 2: Mark timelog drafts as synced after successful sync
      if (item.module === 'timelog' && item.operation === 'create') {
        const serverId = typeof res.data === 'number' ? res.data : null;
        if (serverId !== null) {
          await db.timelogs.update(item.id, { syncStatus: 'synced', serverId });
        } else {
          console.warn('[SyncWorker] Timelog sync success but invalid serverId:', res.data);
        }
      }
      
      return true;  // Success
    } catch (error: unknown) {
      const apiError = classifyApiError(error);

      // personUser backend bug: retry ONCE with id_device stripped (null).
      // This is a known backend issue where a valid payload is rejected when
      // id_device is set; the retry with id_device:null often succeeds.
      if (
        apiError.kind === 'backend_bug' &&
        apiError.bugId === 'personUser' &&
        item.module === 'timelog'
      ) {
        const payloadWithoutDevice = {
          ...(payload as Record<string, unknown>),
          id_device: null,
        };
        try {
          const retryRes = await httpClient.request<number>({ method, url: item.endpoint, data: payloadWithoutDevice });
          await markSuccess(item.id);
          
          // Fix 2: Mark timelog drafts as synced after successful retry
          if (item.module === 'timelog' && item.operation === 'create') {
            const serverId = typeof retryRes.data === 'number' ? retryRes.data : null;
            if (serverId !== null) {
              await db.timelogs.update(item.id, { syncStatus: 'synced', serverId });
            } else {
              console.warn('[SyncWorker] Timelog retry sync success but invalid serverId:', retryRes.data);
            }
          }
          
          return true;  // Retry success
        } catch {
          // Retry also failed — fall through to normal failure logic
        }
      }

      const isPermanent =
        apiError.kind === 'validation' ||
        apiError.kind === 'authorization' ||
        item.attempts + 1 >= MAX_ATTEMPTS;

      await markFailed(item.id, apiError.message, isPermanent);

      if (isPermanent && item.module === 'timelog') {
        await db.timelogs.update(item.id, { syncStatus: 'failed' });
      }

      return false;  // Failure
    }
  }

  private invalidateAfterSync(module: SyncQueueItem['module']): void {
    if (module === 'timelog') {
      void this.queryClient.invalidateQueries({ queryKey: ['timelogs'] });
    } else if (
      module === 'employee' ||
      module === 'face_embedding_v2' ||
      module === 'card_enrollment' ||
      module === 'pin_enrollment'
    ) {
      // CRITICAL: refetchQueries instead of just invalidate to force the
      // CompositeEmployeeRepository to fetch from backend and update Dexie.
      // Without this, consecutive offline enrollments (create + update + update)
      // would sync to backend correctly, but Dexie would keep the stale first
      // enrollment data, causing the UI to show old photo/badge after sync.
      void this.queryClient.refetchQueries({ 
        queryKey: ['employees', 'list'],  // Only refetch list queries (byAccessRole)
        type: 'active',  // Only refetch mounted queries
      });
    } else if (module === 'access_role') {
      void this.queryClient.invalidateQueries({ queryKey: ['accessRoles'] });
    }
  }
}
